import {
    Divider, FormControl, FormControlLabel, FormGroup, FormHelperText, Grid, Typography,
} from '@mui/material';
import { Mail, Lock } from '@mui/icons-material';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { LoggedInUser } from '../../../../types/auth.types';
import { TagViewModel } from '../../../../types/tags.types';
import { getLoggedUser, registerUser } from '../../../api/auth.api';
import { reverseGeocode } from '../../../api/geo.api';
import { postUserSettings } from '../../../api/user.api';
import { emailPattern } from '../../../utils/forms.utils';
import { trackSmartAgentRegistrationComplete } from '../../../analytics/gtmEvents';
import { GeoAutocomplete, LocationOption, TagsSearchAutocomplete } from '../../components/AutocompleteRemote';
import GoogleLogin from '../../components/GoogleLogin';
import Alert from '../../foundation/Alert';
import Button from '../../foundation/Button';
import Checkbox from '../../foundation/Checkbox';
import RegistrationTermsText from '../../components/RegistrationTermsText/RegistrationTermsText';
import CustomBR from '../../foundation/CustomBR';
import RemovableTag from '../../foundation/RemovableTag';
import Select from '../../foundation/Select';
import TextField from '../../foundation/TextField';
import { SMART_AGENT_NOTIFICATION_TYPE } from '../../../../common/smart_agent_notification_type.enum';

export type RegisterSmartAgentProps = {
    defaultTags?: TagViewModel[]
    defaultLocation?: LocationOption
    /**
     * default true
     */
    tagsMandatory?: boolean
    onRegistrationComplete?: (user: LoggedInUser) => void
    onSmartAgentRegistrationComplete?: (userSettings, tags: TagViewModel[], distance: number) => void
    showDefaultText?: boolean
    user?: LoggedInUser
}

const PENDING_SMART_AGENT_KEY = 'pendingSmartAgentRegistration';

const defaultCity = {
    formatted_address: 'תל אביב',
    latitude: 32.0852999,
    longitude: 34.78176759,
} as LocationOption;

interface IFormRegister {
    email?: string
    password?: string
    distance: number
    smartAgentNotificationType: number
    termsAccepted?: boolean
}

type PendingSmartAgentRegistration = {
    location: LocationOption
    tags: TagViewModel[]
    distance: number
    smartAgentNotificationType: number
};

const agentOptions = [
    { value: SMART_AGENT_NOTIFICATION_TYPE.CANDIDATE_IMMEDIATELY, text: 'מיידי - מספר פעמים ביום' },
    { value: SMART_AGENT_NOTIFICATION_TYPE.CANDIDATE_DAILY, text: 'יומי - כל הבוקר' },
    { value: SMART_AGENT_NOTIFICATION_TYPE.CANDIDATE_WEEKLY, text: 'שבועי - ראשון בבוקר' },
];

const RegisterSmartAgent = (props: RegisterSmartAgentProps): JSX.Element => {
    const [location, setLocation] = useState<LocationOption>(props.defaultLocation || defaultCity);
    const [tags, setTags] = useState<TagViewModel[]>(props.defaultTags || []);
    const [formErr, setFormErr] = useState<string>(null);
    const [useEmailPassword, setUseEmailPassword] = useState<boolean>(false);
    const googleLoginRef = useRef<HTMLButtonElement>(null);
    const {
        register, handleSubmit, formState: { errors }, watch, trigger,
    } = useForm<IFormRegister>();

    const buildUserSettingsPayload = (
        data: IFormRegister,
        activeTags: TagViewModel[],
        activeLocation: LocationOption = location,
    ) => {
        const userSettingsData = {
            latitude: activeLocation.latitude,
            longitude: activeLocation.longitude,
            formatted_address: activeLocation.formatted_address,
            job_distance: data.distance,
            tags: activeTags.map(t => t.tagid),
            smartAgentNotificationType: data.smartAgentNotificationType,
        };
        if (activeTags.length) {
            userSettingsData.job_distance = data.distance;
            userSettingsData.tags = activeTags.map(t => t.tagid);
        }
        return userSettingsData;
    };

    const savePendingRegistration = (data: IFormRegister) => {
        const pending: PendingSmartAgentRegistration = {
            location,
            tags,
            distance: data.distance,
            smartAgentNotificationType: data.smartAgentNotificationType,
        };
        sessionStorage.setItem(PENDING_SMART_AGENT_KEY, JSON.stringify(pending));
    };

    const applySmartAgentSettings = async (
        user: LoggedInUser,
        data: IFormRegister,
        activeTags: TagViewModel[] = tags,
        activeLocation: LocationOption = location,
    ) => {
        const userSettingsRes = await postUserSettings(
            { userid: user.userid },
            buildUserSettingsPayload(data, activeTags, activeLocation),
            false,
        );
        if (window.location.pathname === '/qsa') {
            trackSmartAgentRegistrationComplete({ userid: user.userid });
        }
        if (props.onSmartAgentRegistrationComplete) {
            props.onSmartAgentRegistrationComplete(userSettingsRes.data, activeTags, data.distance);
        }
    };

    const validateSmartAgentFields = (): boolean => {
        if (props.tagsMandatory !== false && !tags.length) {
            setFormErr('חובה לבחור תפקידים או תחומים לסוכן החכם');
            return false;
        }
        setFormErr(null);
        return true;
    };

    const onSubmit = async (data: IFormRegister) => {
        if (!validateSmartAgentFields()) return;

        try {
            window.showBackdrop();
            let { user } = props;
            if (!user) {
                const userRes = await registerUser({
                    email: data.email,
                    password: data.password,
                    phone: '',
                    smartAgentByActivity: false,
                });
                user = userRes.data;
                if (props.onRegistrationComplete) props.onRegistrationComplete(user);
            }
            await applySmartAgentSettings(user, data);
        } catch (error) {
            setFormErr(error.message);
        }
        window.hideBackdrop();
    };

    const onGoogleAuthSuccess = async () => {
        const pendingRaw = sessionStorage.getItem(PENDING_SMART_AGENT_KEY);
        if (!pendingRaw) return;

        try {
            window.showBackdrop();
            const pending = JSON.parse(pendingRaw) as PendingSmartAgentRegistration;
            const userRes = await getLoggedUser();
            const user = userRes.data;
            setLocation(pending.location);
            setTags(pending.tags);
            sessionStorage.removeItem(PENDING_SMART_AGENT_KEY);
            if (props.onRegistrationComplete) props.onRegistrationComplete(user);
            await applySmartAgentSettings(
                user,
                {
                    distance: pending.distance,
                    smartAgentNotificationType: pending.smartAgentNotificationType,
                },
                pending.tags,
                pending.location,
            );
        } catch (error) {
            setFormErr(error.message);
        } finally {
            window.hideBackdrop();
        }
    };

    const updateInitialLocation = () => {
        if (props.defaultLocation) return;
        window.navigator.geolocation.getCurrentPosition(async position => {
            const addressRes = await reverseGeocode({
                lat: position.coords.latitude,
                lng: position.coords.longitude,
            });
            setLocation(addressRes.data as LocationOption);
        });
    };
    useEffect(updateInitialLocation, []);

    const emailInput = register('email', { required: useEmailPassword && !props.user, pattern: emailPattern });
    const passwordInput = register('password', { required: useEmailPassword && !props.user, minLength: 6 });
    const termsAcceptedInput = register('termsAccepted', { required: !props.user });
    const distanceInput = register('distance', { required: true, valueAsNumber: true });
    const smartAgentNotificationInput = register('smartAgentNotificationType', { required: true, valueAsNumber: true });

    const renderTermsCheckbox = () => (
        <FormGroup>
            <FormControlLabel
                required
                {...termsAcceptedInput}
                inputRef={termsAcceptedInput.ref}
                control={<Checkbox name="termsAccepted" />}
                label="אישור התקנון ותנאי השימוש"
            />
            <FormHelperText>
                <RegistrationTermsText />
            </FormHelperText>
            {errors.termsAccepted && <FormHelperText error>יש לאשר את התקנון ותנאי השימוש</FormHelperText>}
        </FormGroup>
    );

    return <div>
        {formErr && <><Alert severity="error">{formErr}</Alert><CustomBR /></>}
        {props.showDefaultText && <>
            <Typography>הירשמו לסוכן חכם וקבלו למייל בכל בוקר משרות רלוונטיות.</Typography>
            <CustomBR />
        </>}
        <form onSubmit={props.user || useEmailPassword ? handleSubmit(onSubmit) : (ev => { ev.preventDefault(); })}>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <GeoAutocomplete
                        size="small"
                        onChange={(_ev, val: LocationOption) => setLocation(val)}
                        helperText="המיקום ממנו נחפש לך משרות"
                        defaultValue={location?.formatted_address} />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        size="small"
                        defaultValue={20}
                        required
                        {...distanceInput}
                        inputRef={distanceInput.ref}
                        name="distance"
                        id="distance"
                        label="מרחק"
                        type="number"
                        fullWidth
                        error={!!errors.distance}
                        helperText={errors.distance ? 'יש להזין מרחק' : 'מרחק בק"מ'}
                    />
                </Grid>
            </Grid>
            <CustomBR />
            <FormControl fullWidth>
                <Select
                    size="small"
                    name="smartAgentNotificationType"
                    {...smartAgentNotificationInput}
                    inputRef={smartAgentNotificationInput.ref}
                    required fullWidth options={agentOptions} />
                <FormHelperText>בחר את סוג ההתראות למייל</FormHelperText>
            </FormControl>
            <CustomBR />
            <Typography>בחר תחומים ותפקידים מתאימים לסוכן החכם:</Typography>
            <CustomBR />
            {tags.length > 0 && <>
                {tags.map(tag => <RemovableTag
                    key={tag.tagid}
                    onRemoveClick={() => setTags(tags.filter(t => t.tagid !== tag.tagid))}
                    text={tag.tag}
                />)}
                <CustomBR /></>}
            <TagsSearchAutocomplete
                filterItems={tags.map(t => t.tag)}
                clearOnChange
                onChange={(_ev, val: TagViewModel) => { setTags([...tags, val]); }} />
            <CustomBR />

            {!props.user && <>
                {renderTermsCheckbox()}
                <CustomBR />

                {!useEmailPassword && <>
                    <GoogleLogin
                        ref={googleLoginRef}
                        flow="register"
                        smartAgentByActivity={false}
                        onAuthSuccess={onGoogleAuthSuccess}
                        isAllowedToOpenPopup={async () => {
                            if (!validateSmartAgentFields()) return false;
                            await trigger(['termsAccepted', 'distance', 'smartAgentNotificationType']);
                            const termsAccepted = watch('termsAccepted', false);
                            if (!termsAccepted) return false;
                            savePendingRegistration(watch());
                            return true;
                        }}
                    />
                    <CustomBR />
                    <Grid container justifyContent="space-between" alignItems="center">
                        <Grid item xs={5}><Divider /></Grid>
                        <Grid item><Typography variant="body1">או</Typography></Grid>
                        <Grid item xs={5}><Divider /></Grid>
                    </Grid>
                    <CustomBR />
                    <Button
                        type="button"
                        onClick={e => {
                            e.preventDefault();
                            setUseEmailPassword(true);
                        }}
                        variant="text"
                        fullWidth
                    >
                        הרשמה עם מייל וסיסמא במקום
                    </Button>
                </>}

                {useEmailPassword && <>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                size="small"
                                required
                                {...emailInput}
                                inputRef={emailInput.ref}
                                name="email"
                                id="email"
                                label="כתובת אימייל"
                                type="email"
                                fullWidth
                                autoComplete="email"
                                error={!!errors.email}
                                helperText={errors.email && 'כתובת המייל אינה תקינה'}
                                InputProps={{
                                    endAdornment: <Mail />,
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                required
                                size="small"
                                {...passwordInput}
                                inputRef={passwordInput.ref}
                                name="password"
                                id="password"
                                label="בחר סיסמא"
                                type="password"
                                autoComplete="new-password"
                                fullWidth
                                error={!!errors.password}
                                helperText={errors.password && 'הסיסמא אינה תקינה, לפחות 6 תווים'}
                                InputProps={{
                                    endAdornment: <Lock />,
                                }}
                            />
                        </Grid>
                    </Grid>
                    <CustomBR />
                    <Button type="submit" color="primary" fullWidth>סיום והרשמה</Button>
                    <CustomBR />
                    <Grid container justifyContent="space-between" alignItems="center">
                        <Grid item xs={5}><Divider /></Grid>
                        <Grid item><Typography variant="body1">או</Typography></Grid>
                        <Grid item xs={5}><Divider /></Grid>
                    </Grid>
                    <CustomBR />
                    <Button
                        type="button"
                        onClick={e => {
                            e.preventDefault();
                            setUseEmailPassword(false);
                        }}
                        variant="text"
                        fullWidth
                    >
                        חזרה להרשמה עם Google
                    </Button>
                </>}
            </>}

            {props.user && <>
                <Button type="submit" color="primary" fullWidth>סיום והרשמה</Button>
            </>}
        </form>
    </div>;
};

export default RegisterSmartAgent;
